import { modalStyle } from '@/components/modalStyle';
import { CreateExerciseFactDTO, ExerciseFactDTO } from '@/generated';
import { useCreateExerciseFact, useUpdateExerciseFact } from '@/hooks/exercise-fact-mutation.hooks';
import { objectHasId } from '@/utils/create-mode-helper';
import { Close } from '@mui/icons-material';
import { Box, Button, Checkbox, Divider, Modal, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { GlossaryTermAutocomplete } from '../glossary/GlossaryTermAutocomplete';
import { useState } from 'react';
import { useGlossaryQuery } from '@/hooks/query-hooks';

export interface UpdateExerciseFactModalProps {
  selectedExerciseId?: string;
  exeriseFact?: ExerciseFactDTO | CreateExerciseFactDTO;
  setExerciseFact: (e?: ExerciseFactDTO | CreateExerciseFactDTO) => void;
}

export const UpdateExerciseFactModal = (props: UpdateExerciseFactModalProps) => {
  const { exeriseFact, setExerciseFact, selectedExerciseId } = props;
  const { glossaryTerms } = useGlossaryQuery();

  const isEditMode = exeriseFact ? objectHasId(exeriseFact) : false;
  const createExerciseFactMutation = useCreateExerciseFact();
  const updateExerciseFactMutation = useUpdateExerciseFact();
  const [selectedGlossaryTermId, setSelectedGlossaryTermId] = useState<string>();

  return exeriseFact ? (
    <Modal sx={{ marginTop: 5 }} open={exeriseFact !== undefined} onClose={() => setExerciseFact()}>
      <Box sx={{ ...modalStyle }}>
        <Box sx={{ my: 1 }}>
          <Stack direction={'row'}>
            <Typography variant="h5">{isEditMode ? 'Edit Exercise Fact' : 'Create Exercise Fact'} </Typography>
            <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'flex-end' }}>
              <Close
                onClick={() => {
                  setExerciseFact(undefined);
                }}
              />
            </Box>
          </Stack>
        </Box>
        <Stack>
          <TextField
            value={exeriseFact?.name}
            placeholder="Title"
            label="Title"
            aria-label="Title"
            aria-describedby="basic-addon1"
            sx={{ my: 1 }}
            onChange={(e: any) => {
              if (exeriseFact) setExerciseFact({ ...exeriseFact, name: e.target.value });
            }}
          />
          <TextField
            label={'Description'}
            multiline
            sx={{ mb: 2 }}
            maxRows={5}
            defaultValue={exeriseFact.description}
            value={exeriseFact?.description}
            onChange={(e: any) => {
              if (exeriseFact) setExerciseFact({ ...exeriseFact, description: e.target.value });
            }}
          />
          <Tooltip title="Only available if the exercise has already been created">
            <Stack direction={'row'} sx={{ mb: 2, gap: 1, alignItems: 'center' }}>
              <Checkbox
                disabled={selectedExerciseId === undefined}
                checked={exeriseFact.exerciseId !== undefined}
                onChange={() => {
                  if (exeriseFact.exerciseId === undefined) setExerciseFact({ ...exeriseFact, exerciseId: selectedExerciseId });
                  else setExerciseFact({ ...exeriseFact, exerciseId: undefined });
                }}
              />
              <Typography>Use only for this exercise</Typography>
            </Stack>
          </Tooltip>
        </Stack>
        <Typography>Linked Glossary Terms</Typography>
        <Stack direction={'row'}>
          <GlossaryTermAutocomplete
            setGlossaryTermId={(id) => {
              setSelectedGlossaryTermId(id);
            }}
          />
          <Button
            sx={{}}
            onClick={() => {
              if (selectedGlossaryTermId) setExerciseFact({ ...exeriseFact, glossaryIds: [...(exeriseFact?.glossaryIds || []), selectedGlossaryTermId] });
              setSelectedGlossaryTermId(undefined);
            }}
          >
            Add
          </Button>
        </Stack>
        {exeriseFact?.glossaryIds?.map((glossaryTermId) => {
          const term = glossaryTerms?.find((gt) => gt.id === glossaryTermId);

          return (
            <Stack sx={{ my: 1, gap: 10, flexDirection: 'row', alignItems: 'center' }}>
              <Typography sx={{}}>{term?.name}</Typography>
              <Button
                onClick={() => {
                  setExerciseFact({
                    ...exeriseFact,
                    glossaryIds: exeriseFact.glossaryIds?.filter((gid) => term?.id !== gid),
                  });
                }}
              >
                Remove
              </Button>
            </Stack>
          );
        })}

        <Divider sx={{ m: 1 }} />

        <Box sx={{ m: 1 }}>
          <Button
            onClick={async () => {
              if (exeriseFact !== undefined) {
                let ex = exeriseFact;

                if (objectHasId(ex)) {
                  await updateExerciseFactMutation.mutateAsync(ex);
                } else {
                  await createExerciseFactMutation.mutateAsync(ex);
                }
              }

              setExerciseFact(undefined);
            }}
          >
            {isEditMode ? 'Save' : 'Create'}
          </Button>
          <Button
            onClick={() => {
              setExerciseFact(undefined);
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  ) : (
    <></>
  );
};
